import React, { useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInvoiceStore } from '../../stores/invoiceStore';
import { useCurrentInvoice, useSavedInvoices, useFilteredInvoices } from '../../stores/selectors';
import { LoadingState } from '../../types/invoice';
import type { PaymentStatus } from '@dutchinvoice/types';
import { FileText, Cloud } from 'lucide-react';
import { DeleteConfirmDialog } from '../DeleteConfirmDialog';
import { useTranslation } from '../../hooks/useTranslation';
import { PaymentStatusFilter, type FilterStatus } from '../PaymentStatus/PaymentStatusFilter';
import { PeriodSelectorFilter } from '../PeriodSelector';
import { useAuthStore } from '../../stores/authStore';
import { CloudButton } from '../Auth/CloudButton';
import { InvoiceOverviewCard } from './InvoiceOverviewCard';

const EmptyState: React.FC<{ filter: FilterStatus }> = React.memo(({ filter }) => {
  const { t } = useTranslation();
  
  // Helper function to get the appropriate message based on filter
  const getEmptyMessage = (filterType: FilterStatus) => {
    switch (filterType) {
      case 'all':
        return t('overview.emptyFilterDescriptions.all');
      case 'paid':
        return t('overview.emptyFilterDescriptions.paid');
      case 'unpaid':
        return t('overview.emptyFilterDescriptions.unpaid');
      case 'overdue':
        return t('overview.emptyFilterDescriptions.overdue');
      default:
        return t('overview.emptyFilterDescriptions.all');
    }
  };
  
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 text-center">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <FileText className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
      </motion.div>
      <motion.div
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('overview.emptyTitle')}</h3>
        <p className="text-gray-500 max-w-sm mx-auto">
          {getEmptyMessage(filter)}
        </p>
      </motion.div>
    </div>
  );
});

const InvoiceContent = React.memo(({ onDelete }: { onDelete: (e: React.MouseEvent, invoiceNumber: string) => void }) => {
  const savedInvoices = useSavedInvoices();
  const currentInvoice = useCurrentInvoice();
  const { 
    loadInvoice, 
    copyInvoice, 
    updatePaymentStatus, 
    loadingState, 
    error,
    currentPeriod,
    setCurrentPeriod,
    isLoadingPeriod,
    fetchInvoicesByPeriod,
  } = useInvoiceStore();
  const [currentFilter, setCurrentFilter] = React.useState<FilterStatus>('all');
  
  // Use the new selector for filtered invoices
  const filteredInvoices = useFilteredInvoices(currentFilter);
  
  // Unified content state for smooth animations
  const isContentLoading = loadingState === LoadingState.LOADING_LOCAL || isLoadingPeriod;
  const contentKey = isContentLoading ? 'loading' : `${currentPeriod}-${currentFilter}-${filteredInvoices.length}`;
  
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const initialAuthRef = React.useRef(false);
  const previousPeriodRef = React.useRef(currentPeriod);

  // Reset payment filter when switching to planned period
  useEffect(() => {
    if (currentPeriod === 'planned') {
      setCurrentFilter('all');
    }
  }, [currentPeriod]);

  // Fetch invoices when period changes and user is authenticated
  useEffect(() => {
    // Skip the first time user becomes authenticated (handled by App's syncWithCloud)
    if (user && !initialAuthRef.current) {
      initialAuthRef.current = true;
      return;
    }
    
    // Only fetch if period actually changed (avoid fetching when just changing payment filter)
    if (user && currentPeriod !== previousPeriodRef.current) {
      previousPeriodRef.current = currentPeriod;
      fetchInvoicesByPeriod(currentPeriod);
    }
  }, [currentPeriod, user, fetchInvoicesByPeriod]);

  // Memoized handlers
  const scrollToInvoiceContainer = useCallback((offset: number = 0) => {
    const invoiceContainer = document.querySelector('.invoice-container-inner');
    if (invoiceContainer) {
      const rect = invoiceContainer.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const containerTop = rect.top + scrollTop;
      
      window.scrollTo({
        top: containerTop + offset,
        behavior: 'smooth'
      });
    }
  }, []);

  const handleCopyInvoice = useCallback((e: React.MouseEvent, invoiceNumber: string) => {
    e.stopPropagation();
    copyInvoice(invoiceNumber);
    scrollToInvoiceContainer(); // Scroll to top of invoice container
  }, [copyInvoice, scrollToInvoiceContainer]);

  const handleLoadInvoice = useCallback((invoiceNumber: string) => {
    loadInvoice(invoiceNumber);
    // Scroll so the bottom of the screen is 50px above the bottom of invoice container
    setTimeout(() => {
      const invoiceContainer = document.querySelector('.invoice-container-inner');
      if (invoiceContainer) {
        const rect = invoiceContainer.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const containerBottom = rect.top + scrollTop + rect.height;
        const viewportHeight = window.innerHeight;
        
        window.scrollTo({
          top: containerBottom - viewportHeight + 50,
          behavior: 'smooth'
        });
      }
    }, 100); // Small delay to ensure content is loaded
  }, [loadInvoice]);

  const handleUpdatePaymentStatus = useCallback((invoiceNumber: string, status: PaymentStatus) => {
    updatePaymentStatus(invoiceNumber, status);
  }, [updatePaymentStatus]);

  // Sort invoices: drafts first, then by invoice number
  const sortedInvoices = useMemo(() => {
    // No special filtering for planned view anymore
    const invoicesToSort = [...filteredInvoices];
    
    return invoicesToSort.sort((a, b) => {
      const aIsDraft = !a.invoiceNumber;
      const bIsDraft = !b.invoiceNumber;
      
      // Always put drafts first
      if (aIsDraft && !bIsDraft) return -1;
      if (!aIsDraft && bIsDraft) return 1;
      
      // If both are drafts or both are not drafts, sort by invoice number
      return b.invoiceNumber.localeCompare(a.invoiceNumber);
    });
  }, [filteredInvoices]);

  // Get total count excluding empty drafts
  const totalCount = useMemo(() => {
    const uniqueInvoices = new Set([
      ...savedInvoices.map(inv => inv.invoiceNumber),
      ...(sortedInvoices.length > savedInvoices.length ? [currentInvoice.invoiceNumber] : [])
    ]);
    return uniqueInvoices.size;
  }, [savedInvoices, sortedInvoices.length, currentInvoice.invoiceNumber]);

  return (
    <div className="bg-gray-50">
      <div className="max-w-7xl min-h-[560px] mx-auto px-3 xs:px-4 sm:px-6 sm:pb-30 lg:px-8 lg:pb-40 py-4 xs:py-6 sm:py-12">
        <div>
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 sm:gap-6 mb-6">
            <div>
              <h2 className="text-3xl font-medium text-gray-900 logo-font">{t('overview.title')}</h2>
              <span className="text-sm text-gray-500">
                {t('overview.totalCount', { count: totalCount })}
              </span>
            </div>
            <div className="w-full sm:w-auto sm:min-w-[400px]">
              <div className="pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:pb-0">
                <PeriodSelectorFilter
                  currentPeriod={currentPeriod}
                  onPeriodChange={setCurrentPeriod}
                  isLoading={isLoadingPeriod}
                />
                {/* Animate the payment status filter when hiding/showing */}
                <AnimatePresence>
                  {currentPeriod !== 'planned' && (
                    <motion.div 
                      key="payment-status-filter"
                      initial={{ opacity: 0, height: 0, marginTop: 0 }}
                      animate={{ 
                        opacity: 1, 
                        height: 'auto',
                        marginTop: '0.5rem'
                      }}
                      exit={{ 
                        opacity: 0, 
                        height: 0,
                        marginTop: 0,
                        transition: { duration: 0.2 }
                      }}
                      transition={{ 
                        type: "spring", 
                        bounce: 0.2, 
                        duration: 0.4 
                      }}
                    >
                      <PaymentStatusFilter
                        currentFilter={currentFilter}
                        onFilterChange={setCurrentFilter}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
          
          {/* Error state */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-600">{error.message}</p>
            </div>
          )}
          
          {/* Unified animated content */}
          <div className="relative">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={contentKey}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                  duration: 0.4
                }}
                className="space-y-4"
              >
                {isContentLoading ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-500">
                      {isLoadingPeriod 
                        ? 'Loading invoices for selected period...'
                        : 'Loading invoices...'
                      }
                    </p>
                  </div>
                ) : sortedInvoices.length === 0 ? (
                  <EmptyState filter={currentFilter} />
                ) : (
                  <div className="space-y-4">
                    {sortedInvoices.map((invoice, index) => (
                      <InvoiceOverviewCard
                        key={invoice.invoiceNumber || invoice.id || `draft-${index}`}
                        invoice={invoice}
                        isCurrent={invoice.invoiceNumber === currentInvoice.invoiceNumber || (!invoice.invoiceNumber && !currentInvoice.invoiceNumber)}
                        isPlanned={currentPeriod === 'planned'}
                        onLoad={() => handleLoadInvoice(invoice.invoiceNumber || '')}
                        onDelete={(e: React.MouseEvent) => onDelete(e, invoice.invoiceNumber || invoice.id || '')}
                        onCopy={(e: React.MouseEvent) => handleCopyInvoice(e, invoice.invoiceNumber)}
                        onUpdatePaymentStatus={(status: PaymentStatus) => handleUpdatePaymentStatus(invoice.invoiceNumber, status)}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Welcome bar - show only when there are no saved invoices and user is not logged in */}
      {savedInvoices.length === 0 && !user && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="bg-white/95 rounded-2xl border border-gray-200 shadow-lg p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-12">
                <div className="flex items-center gap-3 sm:gap-5">
                  <div className="relative">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.06)]">
                      <Cloud className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-50 border border-white flex items-center justify-center">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 mb-0.5">{t('auth.welcomeBar.title')}</h3>
                    <p className="text-gray-500 text-sm">{t('auth.welcomeBar.description')}</p>
                  </div>
                </div>
                <CloudButton variant="welcome" className="sm:ml-auto" />
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
});

export const InvoiceOverview: React.FC = () => {
  const { deleteInvoice, resetCurrentInvoice } = useInvoiceStore();
  const currentInvoice = useCurrentInvoice();
  const [deleteTarget, setDeleteTarget] = React.useState<string | null>(null);

  // Memoized handlers
  const handleDeleteInvoice = useCallback((e: React.MouseEvent, invoiceNumber: string) => {
    e.stopPropagation();
    setDeleteTarget(invoiceNumber);
  }, []);

  return (
    <>
      <InvoiceContent onDelete={handleDeleteInvoice} />

      {/* Delete confirmation dialog */}
      <DeleteConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            // Check if this is the current invoice and it's a concept
            if (currentInvoice.id === deleteTarget && !currentInvoice.invoiceNumber) {
              // For concept invoices, simply reset to a new invoice
              resetCurrentInvoice();
              
              // Scroll to top of invoice container after deleting concept (same as copy behavior)
              setTimeout(() => {
                const invoiceContainer = document.querySelector('.invoice-container-inner');
                if (invoiceContainer) {
                  const rect = invoiceContainer.getBoundingClientRect();
                  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                  const containerTop = rect.top + scrollTop;
                  
                  window.scrollTo({
                    top: containerTop,
                    behavior: 'smooth'
                  });
                }
              }, 100);
            } else {
              // For saved invoices, delete normally
              deleteInvoice(deleteTarget);
            }
            setDeleteTarget(null);
          }
        }}
        titleKey="dialog.deleteInvoice.title"
        messageKey="dialog.deleteInvoice.message"
      />
    </>
  );
}; 